import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import { handleCallbackAction } from '@domain-group/javascript-utils'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import useCampaignStatuses from '@hooks/useCampaignStatuses'
import type { CampaignParams } from '@hooks/useAgencyMarketing'

import CreateAgencyMarketingCampaignModal from '@modals/CreateAgencyMarketingCampaignModal'
import CreateCampaignModal from '@modals/CreateCampaignModal'
import ImportCampaignModal from '@modals/ImportCampaignModal'
import NewCampaignModal from '@modals/NewCampaignModal'

import type { CampaignModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateAgencyMarketingCampaignModal: false,
  showCreateCampaignModal: false,
  showImportCampaignModal: false,
  showNewCampaignModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void
const callbacks = (
  // eslint-disable-next-line no-undef
  component: keyof typeof componentCallbacks,
  setState: SetState,
) => {
  const componentCallbacks = {
    CreateAgencyMarketingCampaignModal: {
      closeModal: () => setState({ showCreateAgencyMarketingCampaignModal: false }),
      createArtworkWithCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateCampaignModal: {
      closeModal: () => setState({ showCreateCampaignModal: false }),
      createCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ImportCampaignModal: {
      closeModal: () => setState({ showImportCampaignModal: false }),
      importCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    NewCampaignModal: {
      closeModal: () => setState({ showNewCampaignModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const Campaigns = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateAgencyMarketingCampaignModal,
    showCreateCampaignModal,
    showImportCampaignModal,
    showNewCampaignModal,
  } = state

  const filterPayload = useFilters({
    defaultFilters: { campaign_status: 'current' },
    persistFilters: true,
    persistedExternalFilterKeys: ['type'],
  })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
  } = filterPayload

  useCampaignStatuses({ performHttpRequests: true })

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateAgencyMarketingCampaignModal: (payload: {
              agencyMarketingCampaignParams: CampaignParams,
            }) => {
        setModalData('CreateAgencyMarketingCampaignModal', payload)
        setState({ showCreateAgencyMarketingCampaignModal: true })
      },
      showCreateCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('CreateCampaignModal', payload)
        setState({ showCreateCampaignModal: true })
      },
      showImportCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('ImportCampaignModal', payload)
        setState({ showImportCampaignModal: true })
      },
      showNewCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('NewCampaignModal', payload)
        setState({ showNewCampaignModal: true })
      },
      defaultFilters,
    },
  }), [topModalKey, defaultFiltersModifiedAt])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent filterPayload={filterPayload} />

        <CreateAgencyMarketingCampaignModal
          // @ts-expect-error TS2739
          callbacks={callbacks('CreateAgencyMarketingCampaignModal', setState)}
          showModal={showCreateAgencyMarketingCampaignModal}
        />

        <CreateCampaignModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateCampaignModal', setState)}
          showModal={showCreateCampaignModal}
        />

        <ImportCampaignModal
            // @ts-expect-error TS2322
          callbacks={callbacks('ImportCampaignModal', setState)}
          showModal={showImportCampaignModal}
        />

        <NewCampaignModal
          callbacks={callbacks('NewCampaignModal', setState)}
          showModal={showNewCampaignModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Campaigns
